import { bundledDuaRouteDescriptors } from '../packages/content/src/route-manifest'

export const PAGE_DOCUMENT_CACHE_PREFIX = 'page-documents'
export const NEXT_DATA_CACHE_PREFIX = 'next-data'
export const START_URL_CACHE_PREFIX = 'start-url'
export const SHELL_ASSET_CACHE_PREFIX = 'shell-assets'
export const NATIVE_OFFLINE_MANIFEST_CACHE_PREFIX = 'native-offline-manifest'
export const NATIVE_OFFLINE_READY_KEY_PREFIX = 'hayirhah:native-offline-ready:'
export const NATIVE_OFFLINE_SKIP_WAITING_MESSAGE_TYPE =
  'HAYIRHAH_NATIVE_OFFLINE_SKIP_WAITING'
export const NATIVE_OFFLINE_READY_MESSAGE_TYPE = 'HAYIRHAH_NATIVE_OFFLINE_READY'
export const NATIVE_OFFLINE_REQUIRED_ASSETS = [
  '/KuranKerimFontAbay.ttf',
  '/KuranKerimFontLatif.ttf',
  '/Mushaf.ttf',
] as const

const SERVICE_WORKER_BUILD_ID_REQUEST_TYPE = 'HAYIRHAH_GET_BUILD_ID'
const SERVICE_WORKER_BUILD_ID_RESPONSE_TYPE = 'HAYIRHAH_BUILD_ID'
const SERVICE_WORKER_BUILD_ID_TIMEOUT_MS = 1000
const NATIVE_OFFLINE_MANIFEST_URL = '/__hayirhah/native-offline-manifest.json'

export type OfflineRouteBootstrapResult = {
  requestedUrlCount: number
  cachedUrlCount: number
  failedUrls: string[]
  complete: boolean
}

type NextRouteMetadata = {
  buildId?: string
  gsp?: boolean
  gssp?: boolean
  props?: {
    __N_SSG?: boolean
    __N_SSP?: boolean
  }
}

type WarmedRouteDocument = {
  cached: boolean
  assetUrls: string[]
  nextDataUrl: string | null
  response: Response | null
}

type NativeOfflineCacheEntry = {
  cacheName: string
  url: string
}

type NativeOfflineReadinessManifest = {
  buildId: string
  entries: NativeOfflineCacheEntry[]
}

export type NativeOfflineWorkerTarget = {
  worker: ServiceWorker
  isWaiting: boolean
}

/** Narrows an unknown service-worker message to a plain object. */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** Reads the build identifier embedded in the currently rendered Next page. */
export const getCurrentBuildId = (): string | null => {
  if (typeof window === 'undefined') return null

  return (
    (window as unknown as { __NEXT_DATA__?: { buildId?: string } }).__NEXT_DATA__
      ?.buildId || null
  )
}

/** Creates the build-scoped cache name used by the service worker. */
export const getBuildScopedCacheName = (
  prefix: string,
  buildId = getCurrentBuildId(),
): string => (buildId ? `${prefix}-${buildId}` : prefix)

/** Returns the local marker key for a specific verified Next build. */
export const getNativeOfflineReadyKey = (buildId: string): string =>
  `${NATIVE_OFFLINE_READY_KEY_PREFIX}${buildId}`

/** Checks whether a readiness manifest contains valid entries for one build. */
const isNativeOfflineReadinessManifest = (
  value: unknown,
  buildId: string,
): value is NativeOfflineReadinessManifest => {
  if (!isRecord(value) || value.buildId !== buildId || !Array.isArray(value.entries)) {
    return false
  }

  const allowedCacheNames = new Set([
    getBuildScopedCacheName(PAGE_DOCUMENT_CACHE_PREFIX, buildId),
    getBuildScopedCacheName(NEXT_DATA_CACHE_PREFIX, buildId),
    getBuildScopedCacheName(START_URL_CACHE_PREFIX, buildId),
    getBuildScopedCacheName(SHELL_ASSET_CACHE_PREFIX, buildId),
  ])

  const entriesAreValid = value.entries.every(
    (entry) =>
      isRecord(entry) &&
      typeof entry.cacheName === 'string' &&
      allowedCacheNames.has(entry.cacheName) &&
      typeof entry.url === 'string' &&
      entry.url.length > 0,
  )
  if (!entriesAreValid) return false

  const shellAssetCacheName = getBuildScopedCacheName(SHELL_ASSET_CACHE_PREFIX, buildId)
  const entryKeys = new Set(
    value.entries.map((entry) => `${entry.cacheName}:${entry.url}`),
  )

  return (
    value.entries.length > 0 &&
    NATIVE_OFFLINE_REQUIRED_ASSETS.every((url) =>
      entryKeys.has(`${shellAssetCacheName}:${url}`),
    )
  )
}

/** Revalidates the cached readiness manifest and every resource it records. */
export const verifyNativeOfflineCacheManifest = async (
  buildId: string,
): Promise<boolean> => {
  if (typeof window === 'undefined' || !('caches' in window) || !buildId) return false

  try {
    const manifestCache = await caches.open(
      getBuildScopedCacheName(NATIVE_OFFLINE_MANIFEST_CACHE_PREFIX, buildId),
    )
    const manifestResponse = await manifestCache.match(NATIVE_OFFLINE_MANIFEST_URL)
    if (!manifestResponse?.ok) return false

    const manifest = (await manifestResponse.json()) as unknown
    if (!isNativeOfflineReadinessManifest(manifest, buildId)) return false

    for (const entry of manifest.entries) {
      const cache = await caches.open(entry.cacheName)
      const response = await cache.match(entry.url)
      if (!response?.ok) return false
    }

    return true
  } catch {
    return false
  }
}

/** Checks whether the current build remains complete and verified offline. */
export const isNativeOfflineReadyForCurrentBuild = async (): Promise<boolean> => {
  const buildId = getCurrentBuildId()
  if (typeof window === 'undefined' || !buildId) return false

  try {
    if (localStorage.getItem(getNativeOfflineReadyKey(buildId)) !== 'verified') {
      return false
    }

    return await verifyNativeOfflineCacheManifest(buildId)
  } catch {
    return false
  }
}

/** Marks the current build ready only after all required caches were verified. */
export const markNativeOfflineReady = (buildId: string): boolean => {
  if (typeof window === 'undefined') return false

  try {
    localStorage.setItem(getNativeOfflineReadyKey(buildId), 'verified')
    return true
  } catch {
    return false
  }
}

/** Reads a worker's embedded build ID before sending lifecycle commands to it. */
export const getServiceWorkerBuildId = (
  worker: ServiceWorker,
  timeoutMs = SERVICE_WORKER_BUILD_ID_TIMEOUT_MS,
): Promise<string | null> => {
  if (
    typeof window === 'undefined' ||
    typeof MessageChannel === 'undefined' ||
    timeoutMs <= 0
  ) {
    return Promise.resolve(null)
  }

  return new Promise((resolve) => {
    const channel = new MessageChannel()
    let timeoutId: number | null = null
    let isSettled = false

    const finish = (buildId: string | null) => {
      if (isSettled) return
      isSettled = true
      if (timeoutId !== null) window.clearTimeout(timeoutId)
      channel.port1.close()
      channel.port2.close()
      resolve(buildId)
    }

    channel.port1.onmessage = (event: MessageEvent<unknown>) => {
      if (!isRecord(event.data)) return
      if (event.data.type !== SERVICE_WORKER_BUILD_ID_RESPONSE_TYPE) return
      finish(typeof event.data.buildId === 'string' ? event.data.buildId : null)
    }

    timeoutId = window.setTimeout(() => finish(null), timeoutMs)

    try {
      worker.postMessage({ type: SERVICE_WORKER_BUILD_ID_REQUEST_TYPE }, [channel.port2])
    } catch {
      finish(null)
    }
  })
}

/** Finds the waiting or active worker that owns the rendered Next build. */
export const findServiceWorkerForBuild = async (
  registration: ServiceWorkerRegistration,
  buildId: string,
): Promise<NativeOfflineWorkerTarget | null> => {
  const candidates: NativeOfflineWorkerTarget[] = []
  if (registration.waiting) {
    candidates.push({ worker: registration.waiting, isWaiting: true })
  }
  if (registration.active) {
    candidates.push({ worker: registration.active, isWaiting: false })
  }

  for (const candidate of candidates) {
    if ((await getServiceWorkerBuildId(candidate.worker)) === buildId) {
      return candidate
    }
  }

  return null
}

/** Lists the minimum route shells required before native readers can open. */
export const NATIVE_OFFLINE_SHELL_ROUTES = [
  '/',
  '/kuran',
  '/dualar',
  '/cevsen',
  '/app-reader',
] as const

/** Lists type-only detail routes for bundled section-based dua descriptors. */
export const NATIVE_OFFLINE_DUA_DETAIL_ROUTES = bundledDuaRouteDescriptors
  .filter((descriptor) => descriptor.view === 'sections')
  .map((descriptor) => `/dualar/${descriptor.slug}`)

/** Returns deterministic shells and dua detail routes; native bridges supply reader content. */
export const getNativeOfflineRequiredRoutes = (): string[] => [
  ...NATIVE_OFFLINE_SHELL_ROUTES,
  ...NATIVE_OFFLINE_DUA_DETAIL_ROUTES,
]

/** Extracts the JSON metadata that Next embeds in each route document. */
const readNextRouteMetadata = (html: string): NextRouteMetadata | null => {
  const match = html.match(
    /<script\b[^>]*\bid=(?:"__NEXT_DATA__"|'__NEXT_DATA__')[^>]*>([\s\S]*?)<\/script>/i,
  )
  if (!match?.[1]) return null

  try {
    return JSON.parse(match[1]) as NextRouteMetadata
  } catch {
    return null
  }
}

/** Resolves whether a route needs a build-scoped Next data request. */
const getNextDataUrl = (
  route: string,
  metadata: NextRouteMetadata,
  expectedBuildId: string,
): string | null => {
  const needsNextData = Boolean(
    metadata.gsp || metadata.gssp || metadata.props?.__N_SSG || metadata.props?.__N_SSP,
  )
  if (!needsNextData) return null

  const routeUrl = new URL(route, window.location.origin)
  const normalizedPath = routeUrl.pathname.replace(/\/$/, '')
  const dataPath = normalizedPath || '/index'
  return `/_next/data/${expectedBuildId}${dataPath}.json${routeUrl.search}`
}

/** Extracts build-scoped JavaScript and CSS assets declared by a route shell. */
const getRouteAssetUrls = (html: string): string[] => {
  const assetUrls = new Set<string>()
  const attributePattern = /(?:src|href)=["']([^"']+)["']/gi

  for (const match of html.matchAll(attributePattern)) {
    const rawUrl = match[1]
    if (!rawUrl) continue

    try {
      const assetUrl = new URL(rawUrl, window.location.origin)
      if (
        assetUrl.origin === window.location.origin &&
        assetUrl.pathname.startsWith('/_next/static/')
      ) {
        assetUrls.add(`${assetUrl.pathname}${assetUrl.search}`)
      }
    } catch {
      // Ignore malformed or external HTML attributes.
    }
  }

  return [...assetUrls]
}

/** Reads and validates one cached route document for the active build. */
const readCachedRouteDocument = async (
  route: string,
  cache: Cache,
  expectedBuildId: string,
): Promise<WarmedRouteDocument | null> => {
  const response = await cache.match(route)
  if (!response?.ok) return null

  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('text/html')) return null

  const html = await response.clone().text()
  const metadata = readNextRouteMetadata(html)
  if (!metadata || metadata.buildId !== expectedBuildId) return null

  return {
    cached: true,
    assetUrls: getRouteAssetUrls(html),
    nextDataUrl: getNextDataUrl(route, metadata, expectedBuildId),
    response,
  }
}

/** Fetches and validates one route document before putting it in Cache Storage. */
const warmRouteDocument = async (
  route: string,
  cache: Cache,
  expectedBuildId: string,
): Promise<WarmedRouteDocument> => {
  const cachedDocument = await readCachedRouteDocument(route, cache, expectedBuildId)
  if (cachedDocument) return cachedDocument

  try {
    const response = await fetch(route, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    })
    const contentType = response.headers.get('content-type') || ''
    if (!response.ok || !contentType.includes('text/html')) {
      return { cached: false, assetUrls: [], nextDataUrl: null, response: null }
    }

    const html = await response.clone().text()
    const metadata = readNextRouteMetadata(html)
    if (!metadata || metadata.buildId !== expectedBuildId) {
      return { cached: false, assetUrls: [], nextDataUrl: null, response: null }
    }

    await cache.put(route, response.clone())
    return {
      cached: true,
      assetUrls: getRouteAssetUrls(html),
      nextDataUrl: getNextDataUrl(route, metadata, expectedBuildId),
      response,
    }
  } catch {
    return { cached: false, assetUrls: [], nextDataUrl: null, response: null }
  }
}

/** Fetches and validates one Next data response before caching it. */
const warmNextDataUrl = async (url: string, cache: Cache): Promise<boolean> => {
  const cachedResponse = await cache.match(url)
  if (cachedResponse?.ok) {
    const cachedContentType = cachedResponse.headers.get('content-type') || ''
    if (cachedContentType.includes('application/json')) return true
  }

  try {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    const contentType = response.headers.get('content-type') || ''
    if (response.ok && contentType.includes('application/json')) {
      await cache.put(url, response.clone())
      return true
    }
  } catch {
    // The caller records this route as incomplete and retries on the next run.
  }

  return false
}

/** Fetches one shell asset into the build-scoped asset cache. */
const warmShellAsset = async (url: string, cache: Cache): Promise<boolean> => {
  const cachedResponse = await cache.match(url)
  if (cachedResponse?.ok) return true

  try {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: '*/*' },
    })
    if (response.ok) {
      await cache.put(url, response.clone())
      return true
    }
  } catch {
    // The caller records this asset as incomplete and retries on the next run.
  }

  return false
}

/** Collects every cache entry required to revalidate one warmed shell. */
const getWarmedRouteCacheEntries = async (
  routes: string[],
): Promise<NativeOfflineCacheEntry[] | null> => {
  if (typeof window === 'undefined' || !('caches' in window)) return null

  const buildId = getCurrentBuildId()
  if (!buildId) return null

  const uniqueRoutes = [...new Set(routes)]
  if (uniqueRoutes.length === 0) return null

  const pageDocumentCacheName = getBuildScopedCacheName(
    PAGE_DOCUMENT_CACHE_PREFIX,
    buildId,
  )
  const shellAssetCacheName = getBuildScopedCacheName(SHELL_ASSET_CACHE_PREFIX, buildId)
  const startUrlCacheName = getBuildScopedCacheName(START_URL_CACHE_PREFIX, buildId)
  const nextDataCacheName = getBuildScopedCacheName(NEXT_DATA_CACHE_PREFIX, buildId)
  const pageDocumentCache = await caches.open(pageDocumentCacheName)
  const shellAssetCache = await caches.open(shellAssetCacheName)
  const startUrlCache = uniqueRoutes.includes('/')
    ? await caches.open(startUrlCacheName)
    : null
  const nextDataCache = await caches.open(nextDataCacheName)
  const entries = new Map<string, NativeOfflineCacheEntry>()
  const addEntry = (cacheName: string, url: string) => {
    entries.set(`${cacheName}:${url}`, { cacheName, url })
  }

  for (const route of uniqueRoutes) {
    const warmedDocument = await readCachedRouteDocument(
      route,
      pageDocumentCache,
      buildId,
    )
    if (!warmedDocument) return null

    if (!(await pageDocumentCache.match(route))) return null
    addEntry(pageDocumentCacheName, route)

    if (route === '/') {
      if (!startUrlCache || !(await startUrlCache.match('/'))) return null
      addEntry(startUrlCacheName, '/')
    }

    for (const assetUrl of warmedDocument.assetUrls) {
      if (!(await shellAssetCache.match(assetUrl))) return null
      addEntry(shellAssetCacheName, assetUrl)
    }

    if (warmedDocument.nextDataUrl) {
      if (!(await nextDataCache.match(warmedDocument.nextDataUrl))) return null
      addEntry(nextDataCacheName, warmedDocument.nextDataUrl)
    }
  }

  for (const assetUrl of NATIVE_OFFLINE_REQUIRED_ASSETS) {
    if (!(await shellAssetCache.match(assetUrl))) return null
    addEntry(shellAssetCacheName, assetUrl)
  }

  return [...entries.values()]
}

/** Stores the verified cache inventory used for lightweight startup checks. */
export const writeNativeOfflineReadinessManifest = async (
  routes: string[],
): Promise<boolean> => {
  if (typeof window === 'undefined' || !('caches' in window)) return false

  const buildId = getCurrentBuildId()
  if (!buildId) return false

  const entries = await getWarmedRouteCacheEntries(routes)
  if (!entries?.length) return false

  try {
    const manifestCache = await caches.open(
      getBuildScopedCacheName(NATIVE_OFFLINE_MANIFEST_CACHE_PREFIX, buildId),
    )
    await manifestCache.put(
      NATIVE_OFFLINE_MANIFEST_URL,
      new Response(JSON.stringify({ buildId, entries }), {
        headers: { 'Content-Type': 'application/json' },
      }),
    )
    return true
  } catch {
    return false
  }
}

/** Warms route documents, required fonts, and Next data declared by their HTML. */
export const warmRouteShells = async (
  routes: string[],
): Promise<OfflineRouteBootstrapResult> => {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return { requestedUrlCount: 0, cachedUrlCount: 0, failedUrls: [], complete: false }
  }

  const buildId = getCurrentBuildId()
  if (!buildId) {
    return { requestedUrlCount: 0, cachedUrlCount: 0, failedUrls: [], complete: false }
  }

  const uniqueRoutes = [...new Set(routes)]
  if (uniqueRoutes.length === 0) {
    return { requestedUrlCount: 0, cachedUrlCount: 0, failedUrls: [], complete: false }
  }

  const pageDocumentCache = await caches.open(
    getBuildScopedCacheName(PAGE_DOCUMENT_CACHE_PREFIX, buildId),
  )
  const shellAssetCache = await caches.open(
    getBuildScopedCacheName(SHELL_ASSET_CACHE_PREFIX, buildId),
  )
  const startUrlCache = uniqueRoutes.includes('/')
    ? await caches.open(getBuildScopedCacheName(START_URL_CACHE_PREFIX, buildId))
    : null
  const documentResults = await Promise.all(
    uniqueRoutes.map(async (route) => ({
      url: route,
      ...(await warmRouteDocument(route, pageDocumentCache, buildId)),
    })),
  )

  const rootDocument = documentResults.find((result) => result.url === '/')
  if (startUrlCache && rootDocument?.response) {
    await startUrlCache.put('/', rootDocument.response.clone())
  }

  const assetUrls = [
    ...new Set([
      ...NATIVE_OFFLINE_REQUIRED_ASSETS,
      ...documentResults.flatMap(({ assetUrls: routeAssetUrls }) => routeAssetUrls),
    ]),
  ]
  const assetResults = await Promise.all(
    assetUrls.map(async (url) => ({
      url,
      cached: await warmShellAsset(url, shellAssetCache),
    })),
  )
  const nextDataUrls = documentResults.flatMap(({ nextDataUrl }) =>
    nextDataUrl ? [nextDataUrl] : [],
  )
  const nextDataCache =
    nextDataUrls.length > 0
      ? await caches.open(getBuildScopedCacheName(NEXT_DATA_CACHE_PREFIX, buildId))
      : null
  const nextDataResults = nextDataCache
    ? await Promise.all(
        nextDataUrls.map(async (url) => ({
          url,
          cached: await warmNextDataUrl(url, nextDataCache),
        })),
      )
    : []

  const results = [
    ...documentResults.map(({ url, cached }) => ({ url, cached })),
    ...assetResults,
    ...nextDataResults,
  ]
  const failedUrls = results
    .filter((result) => !result.cached)
    .map((result) => result.url)

  return {
    requestedUrlCount: results.length,
    cachedUrlCount: results.length - failedUrls.length,
    failedUrls,
    complete: results.length > 0 && failedUrls.length === 0,
  }
}

/** Verifies every route document and declared Next data file before marking ready. */
export const verifyWarmedRouteShells = async (routes: string[]): Promise<boolean> => {
  return Boolean(await getWarmedRouteCacheEntries(routes))
}
